import {
    Box,
    MenuItem,
    Pagination,
    PaginationProps,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    styled,
} from '@mui/material';
import { ChangeEvent, KeyboardEvent, ReactElement, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { PoolTypography } from '../typography/pool-typography';

const PAGINATION_OPTIONS = [5, 30, 50, 100];
const PAGINATION_DEFAULT = 50;

export interface OverviewTablePaginationProps extends Omit<PaginationProps, 'count' | 'page'> {
    pages?: number;
    /** The last page the list can still deliver; see PaginatedResponse.maxPage. */
    maxPage?: number;
    total?: number;
}

const StyledSelect = styled(Select)(() => ({
    '& > div.MuiSelect-select': {
        padding: 8,
        paddingRight: '32px !important',
        textAlign: 'right',
    },
}));

const StyledTextField = styled(TextField)(() => ({
    width: 47,
    '& .MuiInputBase-input': {
        padding: 8,
        textAlign: 'right',
    },
}));

const PaginationColumn = styled(Box)(({ theme }) => ({
    ...theme.typography.body2,
    flex: 1,
    display: 'flex',
}));

export const OverviewTablePagination = (props: OverviewTablePaginationProps): ReactElement | null => {
    const { pages = 0, maxPage, total, onChange, ...paginationProps } = props;
    /*
     * Pages the list can actually deliver. The pager used to stop at 100 whatever
     * the list held, and the jump field offered every page the count allowed — of
     * 79963 invoices at 50 to a page, page 201 was the first that came back empty
     * because the search engine pages only so deep (#2210, R-17). Both now follow
     * the same figure, and the server says what it is.
     */
    const reachablePages = Math.min(maxPage ?? pages, pages);
    const [searchParams, setSearchParams] = useSearchParams();
    const [pageToJump, setPageToJump] = useState<number>(1);

    const handlePaginationChanged = (e: ChangeEvent<unknown>, page: number): void => {
        searchParams.set('page', page.toString());
        setSearchParams(searchParams);
        onChange?.(e, page);
    };

    const handlePageSizeChanged = (e: SelectChangeEvent<unknown>): void => {
        searchParams.delete('page');
        searchParams.set('limit', Number(e.target.value).toString());
        setSearchParams(searchParams);
    };

    const handlePageJumpChanged = (e: ChangeEvent<HTMLInputElement>): void => {
        const page = Number(e.target.value);
        if (!isNaN(page)) {
            setPageToJump(page);
        }
    };

    const handlePageJump = (e: KeyboardEvent<HTMLDivElement>): void => {
        if (e.key !== 'Enter') {
            return;
        }
        const page = Math.min(Math.max(pageToJump, 1), reachablePages);
        searchParams.set('page', page.toString());
        setSearchParams(searchParams);
        setPageToJump(page);
    };

    const limit = Number(searchParams.get('limit') ?? PAGINATION_DEFAULT);
    const page = Number(searchParams.get('page') ?? 1);

    return (
        <PoolTypography component="span" typography="body2">
            <Stack
                direction="row"
                alignItems="center"
                sx={theme => ({
                    py: theme.tableFooter?.paddingBlock ?? 2,
                    px: theme.tableFooter?.paddingInline ?? 3,
                })}
            >
                <PaginationColumn justifyContent="start">
                    <Stack flexDirection={'row'} alignItems="center" gap={2}>
                        <FormattedMessage id="app.common.rowsPerPage" />
                        <StyledSelect value={limit} onChange={handlePageSizeChanged}>
                            {PAGINATION_OPTIONS.map(option => (
                                <MenuItem value={option} key={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </StyledSelect>
                        {total && (
                            <FormattedMessage
                                id="app.common.rows"
                                values={{
                                    indexStart: (page - 1) * limit + 1,
                                    indexEnd: Math.min((page - 1) * limit + limit, total),
                                    total,
                                }}
                            />
                        )}
                    </Stack>
                </PaginationColumn>
                <PaginationColumn justifyContent="center">
                    <Pagination
                        page={page}
                        count={reachablePages}
                        siblingCount={2}
                        boundaryCount={1}
                        onChange={handlePaginationChanged}
                        {...paginationProps}
                    />
                </PaginationColumn>
                <PaginationColumn justifyContent="end">
                    <Stack flexDirection={'row'} alignItems="center" gap={2}>
                        <FormattedMessage id="app.common.rowJump" />
                        <StyledTextField value={pageToJump} onChange={handlePageJumpChanged} onKeyUp={handlePageJump} />
                    </Stack>
                </PaginationColumn>
            </Stack>
        </PoolTypography>
    );
};
